import {
	type OpenInviteInfo,
	type UpdateOpenInviteResponse,
	type GetOpenInviteInfo,
	type GetOpenInviteStateResponse,
} from '../components/ViralSettings/types';
import { resolveError, tryCacheWithClear } from '../utils';
import { DEFAULT_STARGATE_SERVICE_URL, OPEN_INVITE_URL, getOpenInviteNoteUrl } from './common';
import { fetchJson, putJson, RespCodes } from './helpers';
import { type GetOpenInviteNoteResponse } from './types';

export interface OpenInviteClientInterface {
	getOpenInviteState(product: string, cloudId: string): Promise<GetOpenInviteStateResponse>;
	enableOpenInvite(product: string, cloudId: string): Promise<UpdateOpenInviteResponse>;
}

export class OpenInviteClient implements OpenInviteClientInterface {
	openInviteCache: Map<string, GetOpenInviteInfo>;
	constructor() {
		this.openInviteCache = new Map();
	}
	public async getOpenInviteState(
		product: string,
		cloudId: string,
	): Promise<GetOpenInviteStateResponse> {
		const resourceARI = `ari:cloud:${product}::site/${cloudId}`;
		const path = `${DEFAULT_STARGATE_SERVICE_URL}${OPEN_INVITE_URL}?resource=${resourceARI}`;

		const cacheValue = this.openInviteCache.get(resourceARI);
		if (cacheValue) {
			return { response: cacheValue, cached: true };
		}

		try {
			const resp = await fetchJson<OpenInviteInfo>(path, 'getOpenInviteState');
			const openInviteInfo: GetOpenInviteInfo = {
				...resp,
				getOpenInvite: true,
			};
			tryCacheWithClear(this.openInviteCache, resourceARI, openInviteInfo, 50);
			return {
				response: openInviteInfo,
				cached: false,
			};
		} catch (err) {
			const openInviteInfo: GetOpenInviteInfo = {
				getOpenInvite: false,
				mode: 'REQUEST_ACCESS',
			};
			const { responseCode } = resolveError(err);
			if (
				responseCode &&
				responseCode >= RespCodes.BAD_REQUESTS &&
				responseCode <= RespCodes.CLIENT_CLOSED_REQUEST
			) {
				tryCacheWithClear(this.openInviteCache, resourceARI, openInviteInfo, 50);
			}
			throw err;
		}
	}
	public clearCache = () => {
		this.openInviteCache.clear();
	};
	// Will only update to DIRECT_ACCESS for open invite settings.
	public async enableOpenInvite(
		product: string,
		cloudId: string,
	): Promise<UpdateOpenInviteResponse> {
		const resource = `ari:cloud:${product}::site/${cloudId}`;
		// only need to enable OPEN_INVITE (DIRECT_ACCESS) for our experiment
		const mode = 'DIRECT_ACCESS';
		const path = `${DEFAULT_STARGATE_SERVICE_URL}${OPEN_INVITE_URL}`;
		const request: OpenInviteInfo = {
			resource,
			mode,
		};

		await putJson(path, 'enableOpenInvite', request);
		return {
			success: true,
			enableOpenInvite: true,
		};
	}

	public async getOpenInviteNote(orgId: string): Promise<GetOpenInviteNoteResponse> {
		const url = getOpenInviteNoteUrl(DEFAULT_STARGATE_SERVICE_URL, orgId);
		const response = await fetchJson<GetOpenInviteNoteResponse>(url, 'getOpenInviteNote');
		return response;
	}
}
